import { useState, useEffect, useRef } from 'react';
import InputGroup from 'react-bootstrap/InputGroup';
import { Accordion, Col, Collapse, Form, Row, Table, Container } from 'react-bootstrap';
import LocalizedStrings from 'react-localization';
import { Helmet } from "react-helmet";

import { JobList, ApplicationService, StatisticService, GeneralSettings, Colors, ClientLanguage, getClientLanguageAsString } from '../../Api';
import { useQuery } from '../../Utils';
import { CustomButton, CustomLink, CustomFormControl, CustomFileControlButton, CustomFileControlText, JobTitle, DetailsJobSubtitle, CustomAccordionButton } from '../StyledComponents';
import { LoadingStates } from '../Enums'
import { Legals } from '../Legals';
import { Formik } from "formik";
import * as Yup from "yup";
import { Preview } from '../Preview';
import { LogoImage } from '../LogoImage';
import { ApplicationData, ApplicationDataInterface } from './ApplicationData';
import { characterRestrictionRegExp, characterRestrictionInDocNamesRegExp } from './ValidationRules'
import { getColors } from '../../Utils';

let locStrings = new LocalizedStrings({
    de:
    {
        mandatoryFieldsHint: "Die im Folgenden mit * markierten Felder sind Pflichtfelder.",

        firstName: "Vorname*",
        firstNamePlaceholder: "Vorname eingeben",
        firstNameRequired: "Vorname ist erforderlich",
        firstNameValidationError: "Bitte geben Sie einen gültigen Vornamen an",

        lastName: "Nachname*",
        lastNamePlaceholder: "Nachname eingeben",
        lastNameRequired: "Nachname ist erforderlich",
        lastNameValidationError: "Bitte geben Sie einen gültigen Nachnamen an",

        emailAddress: "E-Mail*",
        emailAddressPlaceholder: "E-Mail eingeben",
        emailAddressRequired: "E-Mail ist erforderlich",
        emailAddressValidationError: "Bitte geben Sie eine gültige E-Mail-Adresse an",

        phoneNumber: "Telefon",
        phoneNumberPlaceholder: "Telefon eingeben",
        phoneNumberValidationError: "Ungültige Telefonnummer",

        filePermittedFileFormats: "Folgende Dateiformate sind erlaubt: {permittedExtensions}",
        fileInvalidFileName: "Ungültiger Dokumentenname",

        cvFile: "Lebenslauf",
        cvFileOptional: " (optional)",
        cvFileRequired: "Lebenslauf ist erforderlich und darf nicht größer als {fileSizeLimit} sein.",
        cvFileSizeError: "Lebenslauf darf nicht größer als {fileSizeLimit} sein.",
        cvFileEmptyError: "Die Datei darf nicht leer sein.",
        cvFileButtonText: "Datei auswählen",
        cvFilePlaceholder: "Keine ausgewählt",
        cvFileSelected:"Lebenslauf",

        additionalFiles: "Weitere Unterlagen (optional) ",
        additionalFilesRequirements: "Die Unterlagen dürfen höchstens {fileNumberLimit} Dateien und zusammen nicht größer als {fileSizeLimit} sein.",
        additionalFilesHint: "Mehrfachauswahl möglich: Maximal {fileNumberLimit} Dateien (Lebenslauf + {fileNumberAdjusted} weitere Dateien) mit insgesamt {fileSizeLimit}",
        additionalFilesButtonText: "Dateien auswählen",
        additionalFilesPlaceholder: "Keine ausgewählt",
        additionalFilesMultipleFilesSelected: " Dateien",
        additionalFilesSelected:"Weitere Unterlagen",

        filesSelectedFiles: "Ausgewählte Dateien ",
        filesDeleteFile: "Entfernen",

        dataProtectionRulesRequired: "Die Datenschutzbestimmungen müssen akzeptiert werden",
        dataProtectionRules: "Datenschutzbestimmungen",
        dataProtectionRulesRead: "* Ich habe die {dataProtectionRulesLink} zur Kenntnis genommen.",

        submitApplication: "Bewerbung absenden",

        metaDescriptionApplicationForm: "Bewerbungsformular - Stellenausschreibung"
    },
    en:
    {
        mandatoryFieldsHint: "The fields marked with * are mandatory.",

        firstName: "First name*",
        firstNamePlaceholder: "Enter first name",
        firstNameRequired: "First name required",
        firstNameValidationError: "Please enter a valid first name",

        lastName: "Surname*",
        lastNamePlaceholder: "Enter surname",
        lastNameRequired: "Surname required",
        lastNameValidationError: "Please enter a valid surname",

        emailAddress: "E-mail*",
        emailAddressPlaceholder: "Enter e-mail address",
        emailAddressRequired: "E-mail address required",
        emailAddressValidationError: "Please enter a valid e-mail address",

        phoneNumber: "Phone",
        phoneNumberPlaceholder: "Enter phone number",
        phoneNumberValidationError: "Invalid phone number",

        filePermittedFileFormats: "The following file formats are allowed: {permittedExtensions}",
        fileInvalidFileName: "Invalid document name",

        cvFile: "CV",
        cvFileOptional: " (optional)",
        cvFileRequired: "CV is required and must not be larger than {fileSizeLimit}.",
        cvFileSizeError: "CV must not be larger than {fileSizeLimit}.",
        cvFileEmptyError: "The file must not be empty.",
        cvFileButtonText: "Browse...",
        cvFilePlaceholder: "No file selected.",
        cvFileSelected:"CV",

        additionalFiles: "Additional documents (optional) ",
        additionalFilesRequirements: "The documents must be no more than {fileNumberLimit} files and together no larger than {fileSizeLimit}.",
        additionalFilesHint: "Multiple selection possible: Maximum {fileNumberLimit} files (CV+ {fileNumberAdjusted} additional files) with a total of {fileSizeLimit}",
        additionalFilesButtonText: "Browse...",
        additionalFilesPlaceholder: "No files selected.",
        additionalFilesMultipleFilesSelected: " files selected.",
        additionalFilesSelected: "Additional documents",

        filesSelectedFiles: "Selected files ",
        filesDeleteFile: "Remove",

        dataProtectionRulesRequired: "The privacy policy must be accepted",
        dataProtectionRules: "privacy policy",
        dataProtectionRulesReadSimple: "* I have read the {0}.",
        dataProtectionRulesRead: "* I have read the {dataProtectionRulesLink}.",

        submitApplication: "Submit application",

        metaDescriptionApplicationForm: "Application form - Job posting"
    }
});

interface FormComponentProps {
    uploadStatus: LoadingStates,
    setUploadStatus: any,
    valuesApplicationDataForm: ApplicationData,
    setValuesApplicationDataForm: any,
    jobList: JobList,
    settings: GeneralSettings,
    logoUrl: string,
    colors: Colors,
}

class FileExt extends File {
    isValid: boolean = true;
    errMessage: string = "";
}

export const FormComponent = (props: FormComponentProps) => {
    const fileInputCV = useRef<HTMLInputElement>(null);
    const fileInputAdditionalFiles = useRef<HTMLInputElement>(null);
    const [fileNameCV, setFileNameCV] = useState(locStrings.cvFilePlaceholder);
    const [fileNamesAdditionalFiles, setFileNamesAdditionalFiles] = useState(locStrings.additionalFilesPlaceholder);
    const query = useQuery();
    const previewMode = query.get("preview") !== null && query.get("preview") === "true";

    const jobList = props.jobList;
    const settings = props.settings;
    const colors = getColors(jobList, jobList.jobs[0]);

    const legalNoticeUrl = jobList.legalNoticeUrl
    const dataSecurityUrl = jobList.dataSecurityUrl
    const listId = jobList.id;

    const [cvDocument, setCvDocument] = useState<FileExt>();
    const [additionalDocuments, setAdditionalDocuments] = useState<FileExt[]>([]);
    const cVoptional = jobList.jobs[0].cVoptional

    setLanguage();

    useEffect(() => {
        document.title = jobList?.jobs[0].title;
        setFileNameCV(locStrings.cvFilePlaceholder);
        setFileNamesAdditionalFiles(locStrings.additionalFilesPlaceholder);
    }, []);

    function getFileSizeAsString(totalBytes: number): string {
        const Gigabyte = 1073741824
        const Megabyte = 1048576
        const Kilobyte = 1024

        if (totalBytes <= 0) return "0 Bytes"

        if (totalBytes >= Gigabyte) {
            return (totalBytes / Gigabyte).toFixed(2) + " GB"
        } else if (totalBytes >= Megabyte) {
            return (totalBytes / Megabyte).toFixed(2) + " MB"
        } else if (totalBytes >= Kilobyte) {
            return (totalBytes / Kilobyte).toFixed(2) + " kB"
        } else {
            return totalBytes + " Bytes"
        }
    }

    function getTotalSizeOfAdditionalDocs(): number {
        if (!additionalDocuments || additionalDocuments?.length === 0) return 0

        let totalSize: number = 0
        for (let i = 0; i < additionalDocuments.length; i++) {
            const element = additionalDocuments[i];
            totalSize += element.size
        }
        return totalSize
    }

    function getTotalSizeOfDocs(): number {
        let totalSizeOfAddititionDocs = getTotalSizeOfAdditionalDocs();
        let cvDocumentSize = cvDocument ? cvDocument.size : 0
        return totalSizeOfAddititionDocs + cvDocumentSize
    }

    function fileNameExists(files1: FileExt[], files2: FileExt[]): boolean {
        if (files1 && files1.length > 0 && files2 && files2.length) {
            for (let i = 0; i < files1.length; i++) {
                for (let j = 0; j < files2.length; j++) {
                    if (files1[i].name === files2[j].name) {
                        files1[i] = files2[j];
                        return true;
                    }
                    if (files2[j].name === cvDocument?.name){
                        return true
                    }
                }
            }
            return false;
        } else  if (files2 && files2.length>0 )
            {
                for (let j = 0; j < files2.length; j++) {
                    if (files2[j].name === cvDocument?.name){
                        return true
                    }
            }
            return false;
        } else {
            return false;
        }
    }

    function cvfileNameExists(files: FileExt[],file:FileExt): boolean {
        if (files && files.length > 0 && file ) {
            for (let i = 0; i < files.length; i++) {
                    if (files[i].name === file.name){
                        return true
                    }
                }
            return false;
        } else {
            return false;
        }
    }

    const getGohiringVariables = () => {
        var url = window.location.href
        var pos = url.indexOf("?pid=");
        if (pos > 0) {
            let vars = url.substring(pos + 5);
            if (vars.length > 0) {
                var pair = vars.split("&it=");
                return pair
            }
        }
        return [];
    }

    const submitForm = (values: ApplicationDataInterface) => {
        console.log(values);

        if (previewMode === true) return;

        // ckeck H.o.n.e.y.p.o.t
        if (values.name.length > 0 || values.email.length > 0) return;


        if (!jobList) return;
        let application = values.getApplication(jobList?.jobs[0].tenant, jobList?.jobs[0].id,listId)

        const data = new FormData()
        data.append('application', JSON.stringify(application))
        if (values.cvFile) data.append('cv', values.cvFile, values.cvFile.name)

        data.append('additionalFilesCount', values.additionalFiles == null ? '0' : values.additionalFiles.length.toString())
        if (values.additionalFiles) {
            for (let i = 0; i < values.additionalFiles.length; i++) {
                const element = values.additionalFiles[i];
                data.append('file' + i, element, element.name)
            }
        }

        props.setUploadStatus(LoadingStates.Pending);
        ApplicationService.postApplication(data).then(result => {
            if (result) {
                props.setUploadStatus(LoadingStates.Success);
                StatisticService.updateCounter(jobList?.jobs[0].tenant, jobList?.jobs[0].id, "FormApplicationCount").then(result => {
                    console.log("");
                }).catch(() => {
                    console.log("");
                });
            }
            else {
                props.setUploadStatus(LoadingStates.Fail);
            }
        }).catch(() => {
            props.setUploadStatus(LoadingStates.Fail);
        });
    }

    const schema = Yup.object().shape({
        firstName: Yup.string()
            .required(locStrings.firstNameRequired)
            .when("settings.isUnicode", {
                is: () => !settings.isUnicode,
                then: Yup.string().matches(characterRestrictionRegExp, locStrings.firstNameValidationError)
            }),
        lastName: Yup.string()
            .required(locStrings.lastNameRequired)
            .when("settings.isUnicode", {
                is: () => !settings.isUnicode,
                then: Yup.string().matches(characterRestrictionRegExp, locStrings.lastNameValidationError)
            }),
        emailAddress: Yup.string()
            .email(locStrings.emailAddressValidationError)
            .required(locStrings.emailAddressRequired),
        phoneNumber: Yup.string()
            .matches(RegExp(settings.applicationPhoneNumberRegex), locStrings.phoneNumberValidationError),
        cvFile: Yup.mixed()
            .when("nullable", {
                is: () => cVoptional,
                then: Yup.mixed().nullable()
            })
            .when("required", {
                is: () => !cVoptional,
                then: Yup.mixed().required(locStrings.formatString(locStrings.cvFileRequired, { fileSizeLimit: getFileSizeAsString(settings.applicationFileSizeLimit) }).toString())
            })
            .test("FileSize", locStrings.formatString(locStrings.cvFileSizeError, { fileSizeLimit: getFileSizeAsString(settings.applicationFileSizeLimit) }).toString(), (file) => {
                if (file) {
                    file.isValid = file.size < settings.applicationFileSizeLimit;
                    return file.isValid;
                }
                else {
                    return true;
                }
            })
            .test("NullByteTest", locStrings.cvFileEmptyError, (file) => {
                if (file) {
                    if (file.isValid !== undefined && file.isValid !== null && !file.isValid) return false;
                    file.isValid = file.size > 0;
                    if (!file.isValid) file.errMessage = locStrings.cvFileEmptyError;
                    return file.isValid;
                }
                else {
                    return true;
                }
            })
            .test("FileFormat", locStrings.formatString(locStrings.filePermittedFileFormats, { permittedExtensions: settings.applicationPermittedExtensions.join(" ") }).toString(), (file) => {
                if (file) {
                    if (file.isValid !== undefined && file.isValid !== null && !file.isValid) return false;
                    file.isValid = settings.applicationPermittedMimeTypes.includes(file.type);
                    if (!file.isValid) file.errMessage = locStrings.formatString(locStrings.filePermittedFileFormats, { permittedExtensions: settings.applicationPermittedExtensions.join(" ") }).toString();
                    return file.isValid;
                }
                else {
                    return true;
                }
            })
            .when("settings.isUnicode", {
                is: () => !settings.isUnicode,
                then: Yup.mixed()
                    .test("FileNameTest",
                        locStrings.fileInvalidFileName,
                        (file) => {
                            if (file) {
                                if (file.isValid !== undefined && file.isValid !== null && !file.isValid) return false;
                                file.isValid = file.name.match(characterRestrictionInDocNamesRegExp) !== null;
                                if (!file.isValid) file.errMessage = locStrings.fileInvalidFileName;
                                return file.isValid;
                            } else {
                                return true;
                            }
                        })
            }),
        additionalFiles: Yup.array()
            .nullable()
            .test("FileSize",
                locStrings.formatString(locStrings.additionalFilesRequirements, { fileNumberLimit: settings.applicationFileNumberLimit, fileSizeLimit: getFileSizeAsString(settings.applicationFileSizeLimit) }).toString(),
                (files) => {
                    if (files && files.length > 0) {
                        let totalSize: number = files.reduce((accumulator, currentFile) => accumulator + currentFile.size, 0);
                        return totalSize <= settings.applicationFileSizeLimit && files.length <= settings.applicationFileNumberLimit;
                    } else {
                        return true;
                    }
                })
            .test("FileFormat",
                locStrings.formatString(locStrings.filePermittedFileFormats, { permittedExtensions: settings.applicationPermittedExtensions.join(" ") }).toString(),
                (files) => {
                    if (files && files.length > 0) {
                        let allValid = true;
                        for (let file of files) {
                            if (file.isValid === undefined || file.isValid === null) file.isValid = true;
                            if (!settings.applicationPermittedMimeTypes.includes(file.type)) {
                                file.isValid = false;
                                file.errMessage = locStrings.formatString(locStrings.filePermittedFileFormats, { permittedExtensions: settings.applicationPermittedExtensions.join(" ") }).toString();
                                allValid = false;
                            }
                        }
                        return allValid;
                    } else {
                        return true;
                    }
                })
            .when("settings.isUnicode", {
                is: () => !settings.isUnicode,
                then: Yup.array().nullable()
                    .test("FileNameTest",
                        locStrings.fileInvalidFileName,
                        (files) => {
                            if (files && files.length > 0) {
                                let allValid = true;
                                for (let file of files) {
                                    if (file.isValid === undefined || file.isValid === null) file.isValid = true;
                                    if (!file.name.match(characterRestrictionInDocNamesRegExp)) {
                                        file.isValid = false;
                                        file.errMessage = locStrings.fileInvalidFileName;
                                        allValid = false;
                                    }
                                }
                                return allValid;
                            } else {
                                return true;
                            }
                        })
            }),
        terms: Yup.bool().required().oneOf([true], locStrings.dataProtectionRulesRequired),
    });

    const initialValues = new ApplicationData();


    const vars = getGohiringVariables();
    if (vars.length > 0) {
        initialValues.pid = vars[0];
        initialValues.it = vars[1];
    };

    function getClientLanguage(): string { 
        return getClientLanguageAsString(jobList?.clientLanguage); 
    } 
    
    function setLanguage(): void {
        switch(jobList?.clientLanguage) { 
            case ClientLanguage.German: { 
               locStrings.setLanguage('de');
               break; 
            } 
            case ClientLanguage.English: { 
               locStrings.setLanguage('en'); 
               break; 
            } 
            // Otherwise "props.clientLanguage" is "ClientLanguage.BrowserSetting" and language used is browser setting dependent.
        } 
    }


    return (
        <>
            {setLanguage()}

            <Helmet>
                <meta
                    name="description"
                    content={locStrings.metaDescriptionApplicationForm}
                />
            </Helmet>

            <Container fluid>
                <Container style={{ maxWidth: "800px", backgroundColor: "white" }}>
                    <Row>
                        <Col md={12} style={{ display: "flex", justifyContent: "right" }}>
                        </Col>
                        <Col md={12}>
                            {<div>
                                <div style={{ paddingTop: "30px" }}>
                                    <Row>
                                        <Col className={"FormJobLogo"} style={{ display: "flex", justifyContent: "left" }}>
                                            <LogoImage url={jobList?.jobs[0].hiringOrganization?.sameAs} listId={listId} logoUrl={props.logoUrl} />
                                        </Col>
                                    </Row>
                                </div>
                                {jobList?.jobs[0] && <JobTitle className={"FormJobTitle"}>
                                    {jobList?.jobs[0].title}
                                    {previewMode && <Preview clientLanguage={jobList?.clientLanguage}/>}
                                </JobTitle>}
                                {jobList?.subtitleOnJobPosting && <DetailsJobSubtitle className={"ListDetailsJobSubtitle"}>
                                    {jobList?.jobs[0].subtitle}
                                </DetailsJobSubtitle>}
                                <Formik
                                    validationSchema={schema}
                                    onSubmit={submitForm}
                                    initialValues={initialValues}>
                                    {({
                                        handleSubmit,
                                        handleChange,
                                        handleBlur,
                                        setFieldValue,
                                        setFieldTouched,
                                        values,
                                        touched,
                                        isValid,
                                        errors,
                                        dirty,
                                    }) => (
                                        <Form noValidate onSubmit={handleSubmit}>
                                            <Row className="mb-3">
                                                <Form.Text muted>{locStrings.mandatoryFieldsHint}</Form.Text>
                                                <Form.Group as={Col} controlId="formFirstName">
                                                    <Form.Label className={"FormFirstNameText"}>{locStrings.firstName}</Form.Label>
                                                    <CustomFormControl className={"FormFirstNameControl"}
                                                        colors={colors}
                                                        placeholder={locStrings.firstNamePlaceholder}
                                                        type="text"
                                                        name="firstName"
                                                        value={values.firstName}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        isValid={touched.firstName && !errors.firstName}
                                                        isInvalid={touched.firstName && errors.firstName}
                                                        maxLength={settings.textInputLimit}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.firstName}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group as={Col} controlId="formLastName">
                                                    <Form.Label className={"FormLastNameText"}>{locStrings.lastName}</Form.Label>
                                                    <CustomFormControl className={"FormLastNameControl"}
                                                        colors={colors}
                                                        placeholder={locStrings.lastNamePlaceholder}
                                                        type="text"
                                                        name="lastName"
                                                        value={values.lastName}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        isValid={touched.lastName && !errors.lastName}
                                                        isInvalid={touched.lastName && errors.lastName}
                                                        maxLength={settings.textInputLimit}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {errors.lastName}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Row>

                                            <Form.Group className="mb-3" controlId="formEmail">
                                                <Form.Label className={"FormEMailText"}>{locStrings.emailAddress}</Form.Label>
                                                <CustomFormControl className={"FormEMailControl"}
                                                    colors={colors}
                                                    placeholder={locStrings.emailAddressPlaceholder}
                                                    type="email"
                                                    name="emailAddress"
                                                    value={values.emailAddress}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    isValid={touched.emailAddress && !errors.emailAddress}
                                                    isInvalid={touched.emailAddress && errors.emailAddress}
                                                    maxLength={settings.emailInputLimit}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.emailAddress}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group className="mb-3" controlId="formPhoneNumber">
                                                <Form.Label className={"FormPhoneNumberText"}>{locStrings.phoneNumber}</Form.Label>
                                                <CustomFormControl className={"FormPhoneNumberControl"}
                                                    colors={colors}
                                                    placeholder={locStrings.phoneNumberPlaceholder}
                                                    type="text"
                                                    name="phoneNumber"
                                                    value={values.phoneNumber}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    isValid={touched.phoneNumber && !errors.phoneNumber}
                                                    isInvalid={errors.phoneNumber}
                                                    maxLength={settings.textInputLimit}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.phoneNumber}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group className="mb-3" controlId="formCvDoc">
                                                <Form.Label className={"FormCVFileText"}>{locStrings.cvFile}{(!cVoptional && "*")}{(cVoptional && locStrings.cvFileOptional)} {cvDocument && "(" + getFileSizeAsString(cvDocument.size) + ")"} </Form.Label>
                                                <CustomFormControl className={"FormCVFileControl"}
                                                    colors={colors}
                                                    type="file"
                                                    name="cvFile"
                                                    ref={fileInputCV}
                                                    onChange={(event) => {
                                                        if (event.target.files[0] && !cvfileNameExists(additionalDocuments,event.target.files[0]))  {
                                                            setCvDocument(event.target.files[0]);
                                                            setFieldTouched("cvFile");
                                                            setFieldValue("cvFile", event.target.files[0]);
                                                            setFileNameCV(event.target.files[0].name);
                                                        }
                                                    }}
                                                    style={{ display: 'none' }}
                                                    isValid={touched.cvFile && !errors.cvFile}
                                                    isInvalid={touched.cvFile && errors.cvFile}
                                                    accept={settings.applicationPermittedExtensions}
                                                />
                                                <InputGroup className="mb-3">
                                                    <CustomFileControlButton className={"FormCVFileControlButton"}
                                                        onClick={() => fileInputCV.current && fileInputCV.current.click()}
                                                        colors={colors}
                                                        // isValid={touched.cvFile && !errors.cvFile}
                                                        // isInvalid={touched.cvFile && errors.cvFile}
                                                        >
                                                        {locStrings.cvFileButtonText}
                                                    </CustomFileControlButton>
                                                    <CustomFileControlText className={"FormCVFileControlText"}
                                                        onClick={() => fileInputCV.current && fileInputCV.current.click()}
                                                        colors={colors}
                                                        value={fileNameCV}
                                                        type="text"
                                                        isValid={touched.cvFile && !errors.cvFile}
                                                        isInvalid={touched.cvFile && errors.cvFile}
                                                        readOnly
                                                    />
                                                </InputGroup>
                                                <Form.Text muted style={{ display: touched.cvFile && errors.cvFile ? "none" : "flex" }}>{locStrings.formatString(locStrings.filePermittedFileFormats, { permittedExtensions: settings.applicationPermittedExtensions.join(" ") }).toString()}</Form.Text>
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.cvFile}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group className="mb-3" controlId="formAdditionalFiles">
                                                <Form.Label className={"FormAdditionalFilesText"}>{locStrings.additionalFiles}{additionalDocuments && getTotalSizeOfAdditionalDocs() > 0 && "(" + getFileSizeAsString(getTotalSizeOfAdditionalDocs()) + ")"}</Form.Label>
                                                <CustomFormControl className={"FormAdditionalFilesControl"}
                                                    colors={colors}
                                                    type="file"
                                                    multiple
                                                    name="additionalFiles"
                                                    ref={fileInputAdditionalFiles}
                                                    onChange={(event) => {

                                                        if (event.target.files?.length) {
                                                            let files: FileExt[] = [];
                                                            if (additionalDocuments.length > 0) {
                                                                files = additionalDocuments.slice();
                                                            }
                                                            if (!fileNameExists(files, Array.from<FileExt>(event.target.files))) {
                                                                files.push(...Array.from<FileExt>(event.target.files));
                                                            }
                                                            setAdditionalDocuments(files);
                                                            setFieldTouched("additionalFiles");
                                                            setFieldValue("additionalFiles", files);
                                                            let numberOfFiles = event.target.files.length;
                                                            if (numberOfFiles == 1) {
                                                                setFileNamesAdditionalFiles(event.target.files[0].name);
                                                            }
                                                            if (numberOfFiles > 1) {
                                                                setFileNamesAdditionalFiles(numberOfFiles.toString() + locStrings.additionalFilesMultipleFilesSelected);
                                                            }
                                                        }

                                                    }}
                                                    style={{ display: 'none' }}
                                                    isValid={touched.additionalFiles && !errors.additionalFiles}
                                                    isInvalid={errors.additionalFiles}
                                                    accept={settings.applicationPermittedExtensions} 
                                                />
                                                <InputGroup className="mb-3">
                                                    <CustomFileControlButton className={"FormAdditionalFilesControlButton--ChooseFileButton-XXXXXXXX"}
                                                        onClick={() => fileInputAdditionalFiles.current && fileInputAdditionalFiles.current.click()}
                                                        colors={colors}
                                                        // isValid={touched.additionalFiles && !errors.additionalFiles}
                                                        // isInvalid={touched.additionalFiles && errors.additionalFiles}
                                                        >
                                                        {locStrings.additionalFilesButtonText}
                                                    </CustomFileControlButton>
                                                    <CustomFileControlText className={"FormAdditionalFilesControlText form-control-static FormLastNameControl-xxxxxxxxxxxx"}
                                                        onClick={() => fileInputAdditionalFiles.current && fileInputAdditionalFiles.current.click()}
                                                        colors={colors}
                                                        value={fileNamesAdditionalFiles}
                                                        type="text"
                                                        isValid={touched.additionalFiles && !errors.additionalFiles}
                                                        isInvalid={touched.additionalFiles && errors.additionalFiles}
                                                        readOnly
                                                    />
                                                </InputGroup>
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.additionalFiles}
                                                </Form.Control.Feedback>
                                                <Form.Text muted style={{ display: errors.additionalFiles ? "none" : "flex" }}>
                                                    {locStrings.formatString(locStrings.additionalFilesHint, { fileNumberLimit: settings.applicationFileNumberLimit, fileNumberAdjusted: settings.applicationFileNumberLimit - 1, fileSizeLimit: getFileSizeAsString(settings.applicationFileSizeLimit) })}
                                                </Form.Text>
                                            </Form.Group>

                                            {/* H.o.n.e.y.p.o.t */}
                                            <div style={{
                                                opacity: 0,
                                                position: "absolute",
                                                top: 0,
                                                left: 0,
                                                height: 0,
                                                width: 0,
                                                zIndex: -1
                                            }}>
                                                <Form.Group id="name">
                                                    <Form.Label>Name</Form.Label>
                                                    <Form.Control autoComplete="0" type="name" id="name" name="name" tabIndex={-1}
                                                        onChange={handleChange}></Form.Control>
                                                </Form.Group>
                                                <Form.Group id="email">
                                                    <Form.Label>Email</Form.Label>
                                                    <Form.Control autoComplete="0" type="email" id="email" name="email" tabIndex={-1}
                                                        onChange={handleChange}></Form.Control>
                                                </Form.Group>
                                            </div>

                                            <div style={{ marginBottom: "16px" }}>
                                                <Collapse in={getTotalSizeOfDocs()>0 }>
                                                    <Accordion defaultActiveKey="0">
                                                        <Accordion.Item eventKey="0">
                                                            <CustomAccordionButton colors={colors}>{locStrings.filesSelectedFiles}({getFileSizeAsString(getTotalSizeOfDocs())} ) </CustomAccordionButton>
                                                            <Accordion.Body >
                                                                <Table>
                                                                    <tbody>
                                                                        {cvDocument &&
                                                                            <tr>
                                                                                <td style={{ wordBreak: "break-all" }} className={cvDocument.isValid ? "" : "text-danger"}><strong>{cvDocument.name}</strong></td>
                                                                                <td>{getFileSizeAsString(cvDocument.size)}</td>
                                                                                <td>{locStrings.cvFileSelected}</td>
                                                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                                                    onClick={() => {
                                                                                        setCvDocument(undefined);
                                                                                        setFieldTouched("cvFile");
                                                                                        setFieldValue("cvFile", null);
                                                                                        let inputElement = document.getElementById("formCvDoc") as HTMLInputElement;
                                                                                        inputElement.value = "";
                                                                                        setFileNameCV(locStrings.cvFilePlaceholder);
                                                                                    }}
                                                                                >{locStrings.filesDeleteFile}</CustomLink></td>
                                                                                <td className="invalid" title={cvDocument.errMessage} style={{ visibility: cvDocument.isValid ? 'hidden' : 'visible' }}></td>
                                                                            </tr>
                                                                        }
                                                                        {additionalDocuments && Array.from(additionalDocuments).map((file: FileExt) =>
                                                                            <tr key={file.name}>
                                                                                <td style={{ wordBreak: "break-all" }} className={file.isValid === undefined || file.isValid ? "" : "text-danger"}>{file.name}</td>
                                                                                <td>{getFileSizeAsString(file.size)}</td>
                                                                                <td>{locStrings.additionalFilesSelected}</td>
                                                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                                                    onClick={() => {
                                                                                        let pos = -1;
                                                                                        for (let i = 0; i < additionalDocuments.length; i++) {
                                                                                            const f: FileExt = additionalDocuments[i]
                                                                                            if (f.name === file.name) {
                                                                                                pos = i;
                                                                                                break;
                                                                                            }
                                                                                        }

                                                                                        if (pos > -1) {
                                                                                            let files: FileExt[] = [];
                                                                                            files = additionalDocuments.slice();
                                                                                            files.splice(pos, 1);

                                                                                            setAdditionalDocuments(files);
                                                                                            setFieldValue("additionalFiles", files);

                                                                                            if (files.length === 0) {
                                                                                                let inputElement = document.getElementById("formAdditionalFiles") as HTMLInputElement;
                                                                                                inputElement.value = "";
                                                                                                setFileNamesAdditionalFiles(locStrings.additionalFilesPlaceholder);
                                                                                            }
                                                                                        }
                                                                                    }}
                                                                                >{locStrings.filesDeleteFile}</CustomLink></td>
                                                                                <td className="invalid" title={file.errMessage} style={{ visibility: file.isValid ? 'hidden' : 'visible' }}></td>
                                                                            </tr>
                                                                        )}
                                                                    </tbody>
                                                                </Table>
                                                            </Accordion.Body>
                                                        </Accordion.Item>
                                                    </Accordion>
                                                </Collapse>
                                            </div>

                                            <Form.Group controlId="privacyCheckbox">
                                                <Form.Check className={"FormDataProtectionRulesControl"}
                                                    required
                                                    name="terms"
                                                    type="checkbox"
                                                    onChange={handleChange}
                                                    isInvalid={touched.terms && !!errors.terms}
                                                    feedback={errors.terms}
                                                    feedbackType="invalid"
                                                    label={<span className={"FormDataProtectionRules"} >{locStrings.formatString(locStrings.dataProtectionRulesRead, { dataProtectionRulesLink: <a href={dataSecurityUrl} target="_blank" rel="noreferrer" >{locStrings.dataProtectionRules}</a> })}</span>}
                                                >
                                                </Form.Check>
                                            </Form.Group>

                                            <div style={{ paddingTop: "30px", display: "flex", justifyContent: "center" }}>
                                                <CustomButton className={"FormButton"}
                                                    variant="primary"
                                                    type="submit"
                                                    colors={colors}
                                                >{locStrings.submitApplication}</CustomButton>
                                            </div>

                                            {/* <div>
                                                {errors && Object.keys(errors).length !== 0 && <small><b>Validierungsfehler zum Debuggen, bitte ignorieren:</b> {JSON.stringify(errors, null, 2)}</small>}
                                            </div> */}

                                            {/* <div>
                                                {<small><b>Debug info: ClientLanguage: </b> {getClientLanguage()}</small>}
                                            </div> */}

                                            <footer style={{ display: "table", margin: "auto", paddingTop: "20px" }}>
                                                <Legals colors={colors} legalNoticeUrl={legalNoticeUrl} clientLanguage={jobList.clientLanguage}/>
                                            </footer>
                                        </Form>
                                    )}
                                </Formik>
                            </div>}
                        </Col>
                    </Row>
                </Container>
            </Container>
        </>
    )
}
